import {useStorage} from '@vueuse/core';
import type {DeliveryDate} from '../interfaces/delivery.date.type';
import type {BaseOrderLine} from '../interfaces/order.line.type';
import {getUniqueDeliveryDatesForOrderLines} from './order.service';
import {isSameConsignment, isSameVarietyAndWeek} from './order.line.service';

export interface ShoppingCartStorageState {
	assortment: string;
	orderLines: BaseOrderLine[];
}

const TOGGLE_KEY = 'toggleShoppingCart';

const SHOPPING_CART_KEY: string = `shoppingcart-20181210-${window.userUniqueId}`;

export const shoppingCartToggle = useStorage(TOGGLE_KEY, false);

export const shoppingCartStorage = useStorage<ShoppingCartStorageState>(SHOPPING_CART_KEY, {
	assortment: 'NETHERLANDS',
	orderLines: []
});

const getOrderLineIndex = (orderLine: BaseOrderLine) => {
	const index = shoppingCartStorage.value.orderLines.findIndex((line: BaseOrderLine) => matchOrderLine(line, orderLine));
	return index >= 0 ? index : null;
}

export const getOrderLinesForDeliveryDate = (week: number, year: number) => {
	return shoppingCartStorage.value.orderLines.filter((orderLine: BaseOrderLine) => {
		return orderLine.week === week && orderLine.year === year;
	})
};

export const updateOrderLineAmount = (orderLine: BaseOrderLine, amount: number) => {
	const newOrderLine = {...orderLine, amount};
	const existingIndex = getOrderLineIndex(orderLine);

	// Explicitly check for null, because 0 is a valid index.
	if (existingIndex === null) {
		return;
	}

	shoppingCartStorage.value.orderLines.splice(existingIndex, 1, newOrderLine);
};

const replaceOrderLineInShoppingCart = (orderLine: BaseOrderLine, newOrderLine: BaseOrderLine) => {
	const existingIndex = getOrderLineIndex(orderLine);

	// Explicitly check for null, because 0 is a valid index.
	if (existingIndex === null) {
		return;
	}

	shoppingCartStorage.value.orderLines.splice(existingIndex, 1, newOrderLine);
};

export const updateOrderLineConsignment = (orderLine: BaseOrderLine, consignment: string | null) => {
	const newOrderLine = {...orderLine, consignment};
	replaceOrderLineInShoppingCart(orderLine, newOrderLine);
};

export const updateOrderLineConsignmentAndAmount = (orderLine: BaseOrderLine, consignment: string | null, amount: number) => {
	const newOrderLine = {...orderLine, consignment, amount: orderLine.amount + amount};
	replaceOrderLineInShoppingCart(orderLine, newOrderLine);
};

export const updateOrderLine = (orderLine: BaseOrderLine) => {
	const existingIndex = getOrderLineIndex(orderLine);

	// Explicitly check for null, because 0 is a valid index.
	if (existingIndex === null) {
		return;
	}

	shoppingCartStorage.value.orderLines.splice(existingIndex, 1, orderLine);
};

const matchOrderLine = (originalOrderLine: BaseOrderLine, givenOrderLine: BaseOrderLine) => {
	return isSameVarietyAndWeek(originalOrderLine, givenOrderLine)
		&& isSameConsignment(originalOrderLine, givenOrderLine);
};

export const removeOrderLinesForDeliveryDate = (deliveryDate: DeliveryDate) => {
	getOrderLinesForDeliveryDate(deliveryDate.week, deliveryDate.year).forEach((orderLine: BaseOrderLine) => {
		removeOrderLineFromShoppingCart(orderLine);
	});
};

export const removeOrderLinesForDeliveryDates = (deliveryDates: DeliveryDate[]) => {
	deliveryDates.forEach((deliveryDate: DeliveryDate) => removeOrderLinesForDeliveryDate(deliveryDate));
};

export const removeOrderLineFromShoppingCart = (orderLine: BaseOrderLine) => {
	const existingIndex = getOrderLineIndex(orderLine);

	// Explicitly check for null, because 0 is a valid index.
	if (existingIndex === null) {
		return;
	}

	shoppingCartStorage.value.orderLines.splice(existingIndex, 1);
};

export const addOrderLineToShoppingCart = (orderLine: BaseOrderLine) => {
	const existingIndex = getOrderLineIndex(orderLine);

	// Explicitly check for null, because 0 is a valid index.
	if (existingIndex === null) {
		shoppingCartStorage.value.orderLines.push(orderLine);
	} else {
		// Add amount to existing order line.
		const existingOrderLine = shoppingCartStorage.value.orderLines[existingIndex];
		const newAmount = existingOrderLine.amount + orderLine.amount;
		updateOrderLineAmount(existingOrderLine, newAmount);
	}
};

export const deliveryDates = () => {
	return getUniqueDeliveryDatesForOrderLines(shoppingCartStorage.value.orderLines);
};

export const getExistingOrderLineWithConsignment = (existingOrderLine: BaseOrderLine, consignment: string | null) => {
	return shoppingCartStorage.value.orderLines.find((orderLine: BaseOrderLine) => {
		return isSameVarietyAndWeek(orderLine, existingOrderLine) &&
			((!orderLine.consignment && !consignment) || (orderLine.consignment === consignment));
	});
};
